import { useEffect, useMemo, useState } from "react";
import { Bell, CalendarDays, ChevronRight, Clock3, CornerDownRight, Plus, Repeat2, X } from "lucide-react";
import Alert from "@/components/foundations/feedback/Alert/Alert";
import { planTaskRepeat } from "@shared/taskRepeat";
import type { RepeatEnd, RepeatPreset, RepeatRule } from "./repeat-options-types";
import { presetRule, repeatPreset, repeatSummary } from "./repeat-options-types";

const DEFAULT_ANCHOR = "2026-12-19T17:15:00";

export type RepeatAnchor = {
  dateTime: string;
  alarm?: boolean;
  duration?: number;
  href?: string;
};

export type RepeatOptionsProps = {
  value: RepeatRule;
  onChange: (value: RepeatRule) => void;
  savedValue?: RepeatRule;
  anchor?: RepeatAnchor;
  savedAnchorDateTime?: string;
  maxOccurrences?: number;
  excludedOccurrenceKeys?: string[];
  savedExcludedOccurrenceKeys?: string[];
  onExcludedOccurrenceKeysChange?: (keys: string[]) => void;
  onSave?: (value: RepeatRule, excludedOccurrenceKeys: string[]) => void;
  isSaving?: boolean;
  onOccurrenceClick?: (dateTime: string) => void;
  disabled?: boolean;
  className?: string;
  showDetails?: boolean;
  onOverviewClick?: () => void;
};

export type RepeatOptionPreviewProps = {
  type?: "instance";
  date: string;
  time?: string;
  alarm?: boolean;
  duration?: number;
  enabled?: boolean;
  href?: string;
  onClick?: () => void;
  onEnabledChange?: (enabled: boolean) => void;
};

type GeneratedOccurrence = { key: string; dateTime: Date };
type GeneratedPlan = { occurrences: GeneratedOccurrence[]; limitReached: boolean; nextOccurrence: Date | null };

const presetOptions: Array<{ value: RepeatPreset; label: string }> = [
  { value: "never", label: "Never" },
  { value: "minute", label: "Every Minute" },
  { value: "hour", label: "Every Hour" },
  { value: "day", label: "Every Day" },
  { value: "week", label: "Every Week" },
  { value: "month", label: "Every Month" },
  { value: "year", label: "Every Year" },
];

function formatDuration(duration?: number): string | null {
  if (duration == null) return null;
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (!hours) return `${minutes}m`;
  if (!minutes) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function formatDate(value: Date, anchor?: Date): string {
  const includeYear = anchor && value.getFullYear() !== anchor.getFullYear();
  return value.toLocaleDateString("en-US", { month: "short", day: "numeric", year: includeYear ? "numeric" : undefined });
}

function formatTime(value: Date): string {
  return value.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatTimeInput(value: Date): string {
  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
}

function formatDateTimeInput(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offset = parsed.getTimezoneOffset() * 60_000;
  return new Date(parsed.getTime() - offset).toISOString().slice(0, 16);
}

function occurrenceKey(value: Date): string {
  return formatDateTimeInput(value.toISOString());
}

function addOccurrence(value: Date, rule: Exclude<RepeatRule, { kind: "none" }>): Date {
  const next = new Date(value);
  if (rule.kind === "interval") {
    next.setMinutes(next.getMinutes() + (rule.unit === "hour" ? rule.every * 60 : rule.every));
    return next;
  }
  if (rule.unit === "day") next.setDate(next.getDate() + rule.every);
  if (rule.unit === "week") next.setDate(next.getDate() + rule.every * 7);
  if (rule.unit === "month") next.setMonth(next.getMonth() + rule.every);
  if (rule.unit === "year") next.setFullYear(next.getFullYear() + rule.every);
  return next;
}

type EditableNumberInputProps = {
  value: number;
  onValueChange: (value: number) => void;
  allowDecimal?: boolean;
  disabled?: boolean;
  label: string;
};

function EditableNumberInput({ value, onValueChange, allowDecimal = false, disabled, label }: EditableNumberInputProps) {
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [focused, value]);

  function normalize(raw: string): number {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, allowDecimal ? parsed : Math.trunc(parsed));
  }

  return (
    <input
      type="number"
      min={0}
      max={10000}
      step={allowDecimal ? "any" : 1}
      value={draft}
      aria-label={label}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onChange={event => {
        const raw = event.target.value;
        setDraft(raw);
        onValueChange(raw === "" ? 0 : normalize(raw));
      }}
      onBlur={() => {
        const normalized = normalize(draft);
        setFocused(false);
        setDraft(String(normalized));
        onValueChange(normalized);
      }}
    />
  );
}

function generatePlan(anchorValue: string, rule: RepeatRule, maxOccurrences: number): GeneratedPlan {
  const anchor = new Date(anchorValue);
  if (Number.isNaN(anchor.getTime())) return { occurrences: [], limitReached: false, nextOccurrence: null };
  const planned = planTaskRepeat(formatDateTimeInput(anchorValue), rule, maxOccurrences);
  const occurrences = planned.map(item => ({ key: item.key, dateTime: new Date(`${item.key}:00`) }));
  if (rule.kind === "none" || rule.every <= 0 || occurrences.length === 0) {
    return { occurrences, limitReached: false, nextOccurrence: null };
  }

  const current = occurrences.at(-1)?.dateTime ?? anchor;
  const nextOccurrence = addOccurrence(current, rule);
  const repeatCount = Math.max(0, occurrences.length - 1);
  const requestedCount = rule.end.kind === "count" ? rule.end.count : Number.POSITIVE_INFINITY;
  const until = rule.end.kind === "until" && rule.end.date ? new Date(rule.end.date) : null;
  const limitReached = repeatCount === maxOccurrences
    && requestedCount > repeatCount
    && (!until || nextOccurrence.getTime() <= until.getTime());
  return { occurrences, limitReached, nextOccurrence: limitReached ? nextOccurrence : null };
}

function endValue(value: RepeatRule): RepeatEnd {
  return value.kind === "none" ? { kind: "count", count: 1 } : value.end;
}

function withEnd(value: RepeatRule, end: RepeatEnd): RepeatRule {
  return value.kind === "none" ? value : { ...value, end };
}

function unitLabel(value: Exclude<RepeatRule, { kind: "none" }>): string {
  return `${value.unit}${value.every === 1 ? "" : "s"}`;
}

export function RepeatOptionPreview({ type, date, time, alarm = false, duration, enabled = true, href, onClick, onEnabledChange }: RepeatOptionPreviewProps) {
  const details = (
    <span className="details">
      {type === "instance" ? <CornerDownRight aria-hidden="true" /> : <CalendarDays aria-hidden="true" />}
      <span>{date}</span>
      {time ? <><span className="separator" aria-hidden="true" /><span>{time}</span></> : null}
      {alarm ? <Bell className="alarm" aria-label="Alarm enabled" /> : null}
      {formatDuration(duration) ? <><span className="separator" aria-hidden="true" /><span>{formatDuration(duration)}</span></> : null}
    </span>
  );

  return (
    <div className="repeat-option-preview" data-enabled={enabled} data-type={type ?? "original"}>
      {href && enabled ? <a href={href}>{details}</a> : <button type="button" className="preview-link" onClick={onClick}>{details}</button>}
      {type === "instance" && onEnabledChange ? (
        <button type="button" className="toggle-occurrence" aria-label={enabled ? `Remove repeat on ${date}` : `Restore repeat on ${date}`} title={enabled ? "Remove repeat" : "Restore repeat"} onClick={() => onEnabledChange(!enabled)}>
          {enabled ? <X aria-hidden="true" /> : <Plus aria-hidden="true" />}
        </button>
      ) : null}
    </div>
  );
}

export default function RepeatOptions({
  value,
  onChange,
  savedValue,
  anchor = { dateTime: DEFAULT_ANCHOR, alarm: true, duration: 47 },
  savedAnchorDateTime,
  maxOccurrences = 20,
  excludedOccurrenceKeys,
  savedExcludedOccurrenceKeys,
  onExcludedOccurrenceKeysChange,
  onSave,
  isSaving = false,
  onOccurrenceClick,
  disabled = false,
  className,
  showDetails = true,
  onOverviewClick,
}: RepeatOptionsProps) {
  const preset = repeatPreset(value);
  const end = endValue(value);
  const [localExcludedKeys, setLocalExcludedKeys] = useState<string[]>([]);
  const excludedKeys = excludedOccurrenceKeys ?? localExcludedKeys;
  const savedExcludedKeys = savedExcludedOccurrenceKeys ?? [];
  const plan = useMemo(() => generatePlan(anchor.dateTime, value, Math.max(1, maxOccurrences)), [anchor.dateTime, maxOccurrences, value]);
  const dirty = savedValue !== undefined && (
    JSON.stringify(value) !== JSON.stringify(savedValue)
    || JSON.stringify([...excludedKeys].sort()) !== JSON.stringify([...savedExcludedKeys].sort())
    || (savedAnchorDateTime !== undefined && savedAnchorDateTime !== formatDateTimeInput(anchor.dateTime))
  );

  function cancelChanges() {
    if (!savedValue) return;
    onChange(savedValue);
    if (excludedOccurrenceKeys === undefined) setLocalExcludedKeys(savedExcludedKeys);
    onExcludedOccurrenceKeysChange?.(savedExcludedKeys);
  }

  function actions() {
    if (!dirty || !onSave) return null;
    return (
      <div className="repeat-actions">
        <button type="button" className="cancel" onClick={cancelChanges} disabled={isSaving}>Cancel</button>
        <button type="button" className="save" onClick={() => onSave(value, excludedKeys)} disabled={isSaving}>Save</button>
      </div>
    );
  }

  function changePreset(next: RepeatPreset) {
    onChange(presetRule(next, value));
  }

  function changeEndKind(kind: RepeatEnd["kind"]) {
    if (value.kind === "none") return;
    if (kind === "immediate") onChange(withEnd(value, { kind: "immediate" }));
    if (kind === "until") onChange(withEnd(value, { kind: "until", date: formatDateTimeInput(addOccurrence(new Date(anchor.dateTime), value).toISOString()) }));
    if (kind === "count") onChange(withEnd(value, { kind: "count", count: 1 }));
    if (kind === "indefinite") onChange(withEnd(value, { kind: "indefinite" }));
  }

  function changeEndAt(raw: string) {
    if (value.kind === "none") return;
    const anchorDate = new Date(anchor.dateTime);
    if (value.kind === "interval") {
      const [hours, minutes] = raw.split(":").map(Number);
      const next = new Date(anchorDate);
      next.setHours(hours, minutes, 0, 0);
      if (next.getTime() <= anchorDate.getTime()) next.setDate(next.getDate() + 1);
      onChange(withEnd(value, { kind: "until", date: formatDateTimeInput(next.toISOString()) }));
      return;
    }
    const firstRepeat = addOccurrence(anchorDate, value);
    const next = new Date(`${raw}T${formatTimeInput(firstRepeat)}`);
    onChange(withEnd(value, { kind: "until", date: formatDateTimeInput(next.toISOString()) }));
  }

  function setOccurrenceEnabled(key: string, enabled: boolean) {
    const next = enabled ? excludedKeys.filter(item => item !== key) : Array.from(new Set([...excludedKeys, key]));
    if (excludedOccurrenceKeys === undefined) setLocalExcludedKeys(next);
    onExcludedOccurrenceKeysChange?.(next);
  }

  const rootClassName = ["repeat-options", className].filter(Boolean).join(" ");
  const anchorDate = new Date(anchor.dateTime);
  const summaryText = value.kind === "none"
    ? "Repeat: Never"
    : `${repeatSummary(value).replace(/^Every/, "Repeat every")} x ${Math.max(0, plan.occurrences.length - 1)}`;

  if (!showDetails) {
    return (
      <button type="button" className={`${rootClassName} overview`} onClick={onOverviewClick}>
        <Repeat2 aria-hidden="true" />
        <span>{summaryText}</span>
        <ChevronRight aria-hidden="true" />
      </button>
    );
  }

  if (value.kind === "none") {
    return (
      <div className="repeat-options-editor">
        <section className={`${rootClassName} collapsed`} aria-label="Repeat options">
          <Repeat2 aria-hidden="true" />
          <select value={preset} onChange={event => changePreset(event.target.value as RepeatPreset)} disabled={disabled} aria-label="Repeat pattern">
            {presetOptions.map(option => <option key={option.value} value={option.value}>Repeat: {option.label}</option>)}
          </select>
          <ChevronRight aria-hidden="true" />
        </section>
        {actions()}
      </div>
    );
  }

  return (
    <section className={rootClassName} aria-label="Repeat options">
      <header className="repeat-header">
        <Repeat2 aria-hidden="true" />
        <span>{summaryText}</span>
      </header>

      <div className="repeat-controls">
        <label>
          <span className="visually-hidden">Repeat pattern</span>
          <select value={preset} onChange={event => changePreset(event.target.value as RepeatPreset)} disabled={disabled}>
            {presetOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label className="number-control">
          <span className="visually-hidden">Repeat interval</span>
          <EditableNumberInput
            value={value.every}
            onValueChange={every => onChange({ ...value, every })}
            allowDecimal={value.kind === "interval"}
            disabled={disabled}
            label="Repeat interval"
          />
          <span>{unitLabel(value)}</span>
        </label>
        <label>
          <span className="visually-hidden">Repeat ending</span>
          <select value={end.kind} onChange={event => changeEndKind(event.target.value as RepeatEnd["kind"])} disabled={disabled}>
            <option value="immediate">End immediately</option>
            <option value="count">End after</option>
            <option value="until">End at</option>
            <option value="indefinite">Indefinite</option>
          </select>
        </label>
        {end.kind === "until" ? (
          <label className="date-control">
            <Clock3 aria-hidden="true" />
            <span className="visually-hidden">End date and time</span>
            <span className="date-value">{value.kind === "interval" ? formatTime(new Date(end.date)) : formatDate(new Date(end.date), new Date(anchor.dateTime))}</span>
            <input
              className="native-date-time"
              type={value.kind === "interval" ? "time" : "date"}
              value={value.kind === "interval" ? formatTimeInput(new Date(end.date)) : end.date.slice(0, 10)}
              onChange={event => changeEndAt(event.target.value)}
              disabled={disabled}
            />
          </label>
        ) : end.kind === "count" ? (
          <label className="number-control">
            <span className="visually-hidden">Number of occurrences</span>
            <EditableNumberInput
              value={end.count}
              onValueChange={count => onChange(withEnd(value, { kind: "count", count }))}
              disabled={disabled}
              label="Number of occurrences"
            />
            <span>times</span>
          </label>
        ) : end.kind === "immediate" ? (
          <div className="empty-control">No repeats</div>
        ) : (
          <div className="empty-control">Max {maxOccurrences} times</div>
        )}
      </div>

      <div className="repeat-preview-list" aria-label="Repeat preview">
        {plan.occurrences.map((occurrence, index) => {
          const enabled = !excludedKeys.includes(occurrence.key);
          return (
            <RepeatOptionPreview
              key={occurrence.key}
              type={index === 0 ? undefined : "instance"}
              date={formatDate(occurrence.dateTime, anchorDate)}
              time={formatTime(occurrence.dateTime)}
              alarm={anchor.alarm}
              duration={anchor.duration}
              enabled={enabled}
              href={index === 0 ? anchor.href : undefined}
              onClick={() => onOccurrenceClick?.(occurrence.key)}
              onEnabledChange={index === 0 ? undefined : nextEnabled => setOccurrenceEnabled(occurrence.key, nextEnabled)}
            />
          );
        })}
      </div>

      {plan.limitReached && plan.nextOccurrence ? (
        <Alert variant="warning">
          Max {maxOccurrences} future tasks reached. Consider making a <a href="#pattern">pattern</a> or a new task at{" "}
          <button type="button" onClick={() => onOccurrenceClick?.(occurrenceKey(plan.nextOccurrence as Date))}>
            {formatDate(plan.nextOccurrence, anchorDate)} • {formatTime(plan.nextOccurrence)}
          </button>.
        </Alert>
      ) : null}
      {actions()}
    </section>
  );
}
